import React, {useState, useEffect} from 'react';
import {Button} from 'epic-diesel-common/src';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import RadioBox from 'epic-diesel-form/src/components/form/RadioBox';
import {priority, buttonTypes} from 'epic-diesel-common/src/constants';
import FormsyForm from 'epic-diesel-form/src/components/external/FormsyForm';
import Input from 'epic-diesel-form/src/components/form/Input';
import {Dialog, DialogContent, DialogActions} from '@epic-ui/epic/src/index';
import {getLocale} from 'epic-common/src/locale';

import taxActions from '../../actions/tax.actions';
import getInfluencersFlowSchema from '../../data/InfluencersFlowSchema';

import {
    Individual,
    SoleProprietor,
    SingleMemberLLC,
    OtherEntity
} from '../../constants/BusinessType';

import {LoadIndicator} from '../../components';

import Message, {getMessage} from '../common/Message';
import styles from './TaxRecordDialog.pcss';

const mapStateToProps = state => ({
    messages: state.get('localization').get('messageProperties'),
    taxState: state.get('tax').toJS()
});

const mapDispatchToProps = {
    fetchTaxInterViewURl: taxActions.getTaxInterviewURL,
    retakeTaxInterViewURL: taxActions.retakeInterview,
    resetDialog: taxActions.resetDialog,
    fetchRecordEmail: taxActions.getRecordEmail
};

const TaxRecordDialog = ({
    messages,
    open,
    onClose,
    isVerified,
    taxState,
    fetchTaxInterViewURl,
    retakeTaxInterViewURL,
    resetDialog,
    fetchRecordEmail
}) => {
    const taxInfoSchema = getInfluencersFlowSchema(getMessage, messages).taxRecord;
    const RecordOptions = [
        {
            text: 'epic.affiliate.settings.tax.profile.dialog.record.single.text',
            value: Individual
        },
        {
            text: 'epic.affiliate.settings.tax.profile.dialog.record.sole.text',
            value: SoleProprietor
        },
        {
            text: 'epic.affiliate.settings.tax.profile.dialog.record.single_llc.text',
            value: SingleMemberLLC
        },
        {
            text: 'epic.affiliate.settings.tax.profile.dialog.record.other.text',
            value: OtherEntity
        }
    ];

    const defaultTaxRecord = {
        profileType: Individual,
        organizationId: null
    };

    const [taxRecord, setTaxRecord] = useState(defaultTaxRecord);
    const [showOrg, setShowOrg] = useState(false);
    // const [showErr, setShowErr] = useState(false);

    useEffect(() => {
        if (taxState.organizationEmail) {
           setTaxRecord({
               ...taxRecord,
               organizationEmail: taxState.organizationEmail
           })
        }
    }, []);

    useEffect(() => {
        if (taxState.organizations && taxState.organizations.length) {
            taxRecord.organizationId = taxState.organizations[0].organizationId;
            fetchRecordEmail(taxRecord.organizationId);
        }
        setTaxRecord({
            ...taxRecord
        });
    }, [taxState.organizations]);

    useEffect(() => {
        setTaxRecord({
            ...taxRecord,
            organizationEmail: taxState.organizationEmail
        });
    }, [taxState.organizationEmail]);

    const handleSetTaxRecord = (name, value) => {
        const newState = {
            ...taxRecord
        };
        newState[name] = value;
        setTaxRecord(newState);
    };

    const getProfileValue = value => {
        return taxRecord.profileType === value;
    };

    const getOrgValue = value => {
        return taxRecord.organizationId === value;
    };

    const onSubmit = () => {
        const isOrg = (OtherEntity === taxRecord.profileType);
        if (isOrg && !showOrg) {
            setShowOrg(true);
            return;
        }
        const locale = getLocale();
        if (!showOrg) {
            delete taxRecord.organizationId;
            delete taxRecord.organizationEmail;
        }
        const fetchUrl = isVerified ? retakeTaxInterViewURL : fetchTaxInterViewURl;
        let checkAvailibility = true;
        if (isVerified) {
            if (taxState.record.organizationEntity) {
                checkAvailibility = isOrg;
            } else {
                checkAvailibility = !isOrg;
            }
        }
        fetchUrl({...taxRecord, checkAvailibility, locale});
    };

    return (
        <Dialog className={styles.taxRecordDialog} open={open}>
            <LoadIndicator messages={messages} isLoading={taxState.loading}>
                <FormsyForm submitForm={onSubmit}>
                    <DialogContent className={styles.dialogContent}>
                        {!showOrg ? (
                            <>
                                <p>
                                    {getMessage(
                                        messages,
                                        'epic.affiliate.settings.tax.profile.dialog.title'
                                    )}
                                </p>
                                <div>
                                    {RecordOptions.map(opt => {
                                        return (
                                            <RadioBox
                                                getValue={() => {
                                                    return getProfileValue(opt.value);
                                                }}
                                                className={styles.radio}
                                                key={opt.value}
                                                value={opt.value === taxRecord.profileType}
                                                name={opt.value}
                                                onChange={() => {
                                                    handleSetTaxRecord('profileType', opt.value);
                                                }}>
                                                <Message code={opt.text} />
                                            </RadioBox>
                                        );
                                    })}
                                </div>
                            </>
                        ) : (
                            <>
                                <p>
                                    {getMessage(
                                        messages,
                                        'epic.affiliate.settings.tax.profile.dialog.title.organization'
                                    )}
                                </p>
                                <div>
                                    {taxState.organizations.map(org => {
                                        return (
                                            <RadioBox
                                                getValue={() => {
                                                    return getOrgValue(org.value);
                                                }}
                                                className={styles.radio}
                                                key={org.value}
                                                value={org.value === taxRecord.organizationId}
                                                name={org.name}
                                                onChange={() => {
                                                    fetchRecordEmail(org.value);
                                                    handleSetTaxRecord('organizationId', org.value);
                                                }}>
                                                <Message code={org.name} />
                                            </RadioBox>
                                        );
                                    })}
                                </div>
                                <div>
                                    <Input
                                        onChange={e => {
                                            handleSetTaxRecord('organizationEmail', e.target.value);
                                        }}
                                        name={taxInfoSchema.organizationEmail.identifier}
                                        value={taxRecord.organizationEmail}
                                        validations={taxInfoSchema.organizationEmail.validations}
                                        validationErrors={taxInfoSchema.organizationEmail.validationMessages}
                                        placeholder={taxInfoSchema.organizationEmail.display.text}
                                        required
                                    />
                                </div>
                                { taxState.dialogError && <p className={styles.errorMessage}><Message code={taxState.dialogError} /></p> }
                            </>
                        )}
                    </DialogContent>
                    <DialogActions className={styles.dialogActions}>
                        <Button
                            type={buttonTypes.RESET}
                            priority={priority.PRIMARY}
                            onClick={() => {
                                onClose();
                                resetDialog();
                                if (taxState.organizations && taxState.organizations.length) {
                                    taxRecord.organizationId = taxState.organizations[0].organizationId;
                                    fetchRecordEmail(taxRecord.organizationId);
                                }
                                setShowOrg(false);
                            }}>
                            {getMessage(
                                messages,
                                'epic.affiliate.common.cancel'
                            )}
                        </Button>
                        <Button type={buttonTypes.SUBMIT} priority={priority.PRIMARY}>
                            {getMessage(
                                messages,
                                'epic.affiliate.common.continue'
                            )}
                        </Button>
                    </DialogActions>
                </FormsyForm>
            </LoadIndicator>
        </Dialog>
    );
};

TaxRecordDialog.propTypes = {
    messages: PropTypes.object,
    open: PropTypes.bool,
    isVerified: PropTypes.bool,
    onClose: PropTypes.func,
    fetchTaxInterViewURl: PropTypes.func,
    retakeTaxInterViewURL: PropTypes.func,
    fetchRecordEmail: PropTypes.func,
    resetDialog: PropTypes.func,
    taxState: PropTypes.object
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TaxRecordDialog);
